/**
 * Custom useParams hook for KeepAlive compatibility
 * 
 * React Router's useParams doesn't work in KeepAliveOutlet because
 * components are rendered outside the Route context.
 * 
 * This hook provides a compatibility layer that:
 * 1. First tries to get params from the custom RouteParamsContext
 * 2. Falls back to react-router's useParams
 */

import { useParams as useReactRouterParams } from 'react-router-dom';
import { useRouteParams } from '@/context/RouteParamsContext';

/**
 * Get route params - works both inside and outside KeepAliveOutlet
 */
export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  const customParams = useRouteParams();
  const routerParams = useReactRouterParams<T>();
  
  // If we have custom params (from KeepAliveOutlet), prefer those
  if (Object.keys(customParams).length > 0) {
    return customParams as T;
  }
  
  // Otherwise fall back to react-router's params
  return routerParams as T;
}

export default useParams;

